/**
 * Persistent storage, requested rather than merely observed.
 *
 * `heartbeat.ts` already reads `navigator.storage.persisted()` and reports it.
 * Reading it is not enough: without a granted persistence promise the browser
 * may evict this origin's IndexedDB under disk pressure, and eviction has no
 * local symptom at all — the computer comes back looking freshly installed, in
 * the middle of an outage, with a day's register work in it.
 *
 * So the bootstrap asks for the promise, and a refusal is a blocking readiness
 * reason rather than a warning. A device that cannot keep what it is given is
 * not offline-ready, however complete its cache looks.
 */
export interface StorageState {
  persisted: boolean
  /** Null when the browser will not estimate — some do not, and that is not a failure. */
  quotaBytes: number | null
  usageBytes: number | null
  supported: boolean
}

export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false

  try {
    // Already granted is a yes; asking again is harmless but pointless.
    if (await navigator.storage.persisted()) return true

    return await navigator.storage.persist()
  } catch {
    return false
  }
}

export async function storageState(): Promise<StorageState> {
  if (!navigator.storage?.estimate) {
    return { persisted: false, quotaBytes: null, usageBytes: null, supported: false }
  }

  try {
    const estimate = await navigator.storage.estimate()

    return {
      persisted: (await navigator.storage.persisted?.()) ?? false,
      quotaBytes: estimate.quota ?? null,
      usageBytes: estimate.usage ?? null,
      supported: true
    }
  } catch {
    return { persisted: false, quotaBytes: null, usageBytes: null, supported: false }
  }
}

/**
 * Whether a write that failed did so because the disk is full, rather than for
 * one of the many other reasons IndexedDB refuses.
 *
 * Worth telling apart: running out of room is the one failure the operator can
 * do something about, and the one where retrying the same step immediately is
 * certain to fail again.
 */
export function isQuotaExceeded(error: unknown): boolean {
  if (!(error instanceof Error)) return false

  return error.name === "QuotaExceededError" || /quota/i.test(error.message)
}
